

import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogClose, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Toggle from "@/components/Toggle";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { X, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import imageCompression from 'browser-image-compression';
import { toast } from "sonner";
import { getProfile, updateProfile } from "@/services/profileService";
import { notifyBillingOfProfileEdit } from "@/services/syProfileSyncService";

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: string;
}

const EditProfileDialog = ({ open, onOpenChange, defaultTab = "profile" }: EditProfileDialogProps) => {
  const navigate = useNavigate();
  const { user, updateAvatarUrl } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    fullName: "",
    timezone: "(GMT +02:00) Jerusalem",
    language: "עברית",
    headline: "",
    aboutMe: "",
    interests: "",
    location: "",
    hobbies: "",
    age: "",
    makeEmailPublic: false,
    preventMessaging: false,
    showInDirectory: true
  });

  const [authData, setAuthData] = useState({
    email: "eldangalperin@gmail.com",
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const [notifications, setNotifications] = useState({
    // פעילות חדשה
    commentsOnPosts: { email: false, inApp: false },
    repliesToComments: { email: false, inApp: false },
    mentions: { email: false, inApp: false },
    directMessages: { email: false, inApp: false },
    weeklyDigest: { email: false, inApp: false },
    likesOnPosts: { email: false, inApp: false },
    likesOnComments: { email: false, inApp: false },
    liveStreams: { email: false, inApp: false },
    newCourseContent: { email: false, inApp: false },
    polls: { email: false, inApp: false },
    gamification: { email: false, inApp: false },
    newMembers: { email: false, inApp: false },
    flaggedContent: { email: true, inApp: true },
    
    // פוסטים חדשים
    allSpaces: { email: false, inApp: false },
    // מרחבים ספציפיים
    healthyHabitsSpace: { email: false, inApp: false },
    nutritionSpace: { email: false, inApp: false },
    fitnessSpace: { email: false, inApp: false },
    mindfulnessSpace: { email: false, inApp: false },
    recipesSpace: { email: false, inApp: false },
    motivationSpace: { email: false, inApp: false },
    workoutPlansSpace: { email: false, inApp: false },
    wellnessSpace: { email: true, inApp: true },
    communitySpace: { email: false, inApp: false },
  });

  const [profileImage, setProfileImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  /**
   * The name as it is PERSISTED, so a save can tell whether the name actually
   * changed. This dialog is a SECOND entry point to the same edit as
   * EditProfileTab (Header menu vs the Profile page tab) — both must announce a
   * rename to StayYoung billing, or renaming from the header silently drifts.
   */
  const savedFullNameRef = useRef<string>("");

  // טעינת פרטי פרופיל בפתיחת הדיאלוג
  React.useEffect(() => {
    if (!open || !user?.id) return;
    const loadProfile = async () => {
      const profile = await getProfile(user.id);
      if (profile) {
        savedFullNameRef.current = profile.display_name || "";
        setFormData(prev => ({
          ...prev,
          fullName: profile.display_name || "",
          headline: profile.headline || "",
          aboutMe: profile.about_me || "",
          interests: profile.pronouns || "",
          location: profile.location || "",
          hobbies: profile.hobbies || "",
          age: profile.age?.toString() || "",
        }));
        setProfileImage(profile.avatar_url);
      }
    };
    loadProfile();
  }, [open, user?.id]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAuthChange = (field: string, value: string) => {
    setAuthData(prev => ({ ...prev, [field]: value }));
  };

  const handleNotificationChange = (key: string, type: 'email' | 'inApp', value: boolean) => {
    setNotifications(prev => ({
      ...prev,
      [key]: {
        ...prev[key as keyof typeof prev],
        [type]: value
      }
    }));
  };

  const turnOffAllNotifications = () => {
    const updatedNotifications = Object.keys(notifications).reduce((acc, key) => {
      acc[key as keyof typeof notifications] = { email: false, inApp: false };
      return acc;
    }, {} as typeof notifications);
    setNotifications(updatedNotifications);
  };

  const handleImageUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setPendingFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setProfileImage(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadAvatar = async (file: File): Promise<string | null> => {
    if (!user) return null;

    const options = {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 800,
      useWebWorker: true,
      initialQuality: 0.85,
    };

    let compressedFile: File;
    try {
      compressedFile = await imageCompression(file, options);
    } catch {
      compressedFile = file;
    }

    const fileExt = compressedFile.name.split('.').pop();
    const fileName = `${user.id}/avatar.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(fileName, compressedFile, { upsert: true });

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('avatars')
      .getPublicUrl(fileName);

    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: data.publicUrl })
      .eq('id', user.id);

    if (updateError) throw updateError;

    await Promise.all([
      supabase
        .from('community_posts')
        .update({ author_image: data.publicUrl })
        .eq('author_id', user.id),
      supabase
        .from('community_comments')
        .update({ author_image: data.publicUrl })
        .eq('author_id', user.id),
    ]);

    return data.publicUrl;
  };

  const handleSave = async () => {
    if (!user?.id) {
      toast.error("לא מחוברים — יש להתחבר מחדש");
      return;
    }
    setIsSaving(true);
    try {
      // שמירת תמונה אם השתנתה
      if (pendingFile) {
        const newUrl = await uploadAvatar(pendingFile);
        if (newUrl) {
          updateAvatarUrl(newUrl);
          setProfileImage(newUrl);
          setPendingFile(null);
        }
      }

      // Tell StayYoung billing BEFORE writing locally. A refusal that lands
      // after the local save leaves the two systems disagreeing about who this
      // member is — the exact drift this call exists to prevent.
      const nextFullName = formData.fullName.trim();
      if (nextFullName !== savedFullNameRef.current) {
        const sync = await notifyBillingOfProfileEdit({ fullName: nextFullName || null });
        if (sync.status === 'refused') {
          toast.error(sync.message);
          return;
        }
      }

      // שמירת שדות הפרופיל למסד
      const success = await updateProfile(user.id, {
        display_name: nextFullName || null,
        headline: formData.headline || null,
        about_me: formData.aboutMe || null,
        pronouns: formData.interests || null,
        location: formData.location || null,
        hobbies: formData.hobbies || null,
        age: formData.age ? parseInt(formData.age) : null,
      });

      if (success) {
        savedFullNameRef.current = nextFullName;
        toast.success("השינויים נשמרו בהצלחה");
        onOpenChange(false);
      } else {
        toast.error("שגיאה בשמירת הפרופיל — נסו שוב");
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("לא הצלחנו לשמור את הפרופיל. נסו שוב.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleDeactivateAccount = () => {
    console.log("ביטול חשבון");
    // כאן יהיה הלוגיקה לביטול החשבון
  };

  const NotificationRow = ({ 
    title, 
    notificationKey, 
    isIndented = false 
  }: { 
    title: string; 
    notificationKey: keyof typeof notifications;
    isIndented?: boolean;
  }) => (
    <div className={`flex items-center justify-between py-3 ${isIndented ? 'pr-8' : ''}`}>
      <span className="text-base">{title}</span>
      <div className="flex items-center gap-8">
        <Checkbox
          checked={notifications[notificationKey]?.email || false}
          onCheckedChange={(checked) => 
            handleNotificationChange(notificationKey as string, 'email', checked as boolean)
          }
        />
        <Checkbox
          checked={notifications[notificationKey]?.inApp || false}
          onCheckedChange={(checked) => 
            handleNotificationChange(notificationKey as string, 'inApp', checked as boolean)
          }
        />
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="!max-w-[95vw] !w-[95vw] max-h-[90vh] overflow-y-auto p-0 app-container"
        position="center"
        hideOverlay={false}
      >
        <DialogTitle className="sr-only">עריכת פרופיל</DialogTitle>
        <DialogDescription className="sr-only">עריכת פרטי הפרופיל האישי</DialogDescription>

        {/* כותרת עם כפתור סגירה */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-semibold">הגדרות חשבון</h2>
          <DialogClose asChild>
            <Button variant="ghost" size="icon" className="tap-target h-8 w-8" aria-label="סגירה">
              <X className="h-4 w-4" />
            </Button>
          </DialogClose>
        </div>

        <div className="p-6">
          <Tabs defaultValue={defaultTab} className="w-full" dir="rtl">
            <TabsList className="mb-8 w-full justify-start h-12">
              <TabsTrigger value="profile" className="text-base px-6">פרופיל</TabsTrigger>
              <TabsTrigger value="notifications" className="text-base px-6">התראות</TabsTrigger>
              <TabsTrigger value="authentication" className="text-base px-6">אימות</TabsTrigger>
            </TabsList>

            <TabsContent value="profile" className="space-y-8 text-right">
              <div>
                <h3 className="text-2xl font-bold mb-6">פרופיל</h3>
                
                {/* תמונת פרופיל */}
                <div className="flex items-center justify-center mb-6">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Avatar className="h-16 w-16 cursor-pointer active:opacity-80 transition-opacity" onClick={handleImageUpload}>
                    {profileImage || user?.avatar_url ? (
                      <AvatarImage src={profileImage || user?.avatar_url || undefined} />
                    ) : (
                      <AvatarFallback className="text-xl bg-primary text-primary-foreground">{user?.display_name?.[0] || "א"}</AvatarFallback>
                    )}
                  </Avatar>
                </div>

                <Separator className="my-6" />

                {/* שם מלא */}
                <div className="space-y-2 text-right">
                  <Label htmlFor="fullName" className="text-base font-medium">
                    שם מלא <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="fullName"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange("fullName", e.target.value)}
                    className="text-right"
                    dir="rtl"
                  />
                </div>

                {/* אזור זמן */}
                <div className="space-y-2 text-right mt-6">
                  <Label className="text-base font-medium">
                    אזור זמן <span className="text-destructive">*</span>
                  </Label>
                  <Select value={formData.timezone} onValueChange={(value) => handleInputChange("timezone", value)}>
                    <SelectTrigger className="text-right" dir="rtl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="(GMT +02:00) Jerusalem">(GMT +02:00) ירושלים</SelectItem>
                      <SelectItem value="(GMT +03:00) Moscow">(GMT +03:00) מוסקבה</SelectItem>
                      <SelectItem value="(GMT +00:00) London">(GMT +00:00) לונדון</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* שפה */}
                <div className="space-y-2 text-right mt-6">
                  <Label className="text-base font-medium">
                    שפה <span className="text-destructive">*</span>
                  </Label>
                  <Select value={formData.language} onValueChange={(value) => handleInputChange("language", value)}>
                    <SelectTrigger className="text-right" dir="rtl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="עברית">עברית</SelectItem>
                      <SelectItem value="English">English</SelectItem>
                      <SelectItem value="العربية">العربية</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* כותרת */}
                <div className="space-y-2 text-right mt-6">
                  <Label htmlFor="headline" className="text-base font-medium">כותרת</Label>
                  <Input
                    id="headline"
                    value={formData.headline}
                    onChange={(e) => handleInputChange("headline", e.target.value)}
                    placeholder="הוסיפו את הכותרת שלכם"
                    className="text-right"
                    dir="rtl"
                  />
                </div>

                {/* שני משפטים על עצמי */}
                <div className="space-y-2 text-right mt-6">
                  <Label htmlFor="aboutMe" className="text-base font-medium">שני משפטים על עצמי</Label>
                  <Textarea
                    id="aboutMe"
                    value={formData.aboutMe}
                    onChange={(e) => handleInputChange("aboutMe", e.target.value)}
                    placeholder="ספרו לנו קצת על עצמכם"
                    className="text-right min-h-[100px]"
                    dir="rtl"
                  />
                </div>

                {/* תעדיפו שיתיחסו אליכם כ */}
                <div className="space-y-2 text-right mt-6">
                  <Label className="text-base font-medium">
                    תעדיפו שיתיחסו אליכם כ <span className="text-destructive">*</span>
                  </Label>
                  <Select value={formData.interests} onValueChange={(value) => handleInputChange("interests", value)}>
                    <SelectTrigger className="text-right" dir="rtl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="גבר">גבר</SelectItem>
                      <SelectItem value="אישה">אישה</SelectItem>
                      <SelectItem value="אחר">אחר</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* כמה גילך */}
                <div className="space-y-2 text-right mt-6">
                  <Label htmlFor="age" className="text-base font-medium">
                    כמה גילך <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="age"
                    value={formData.age}
                    onChange={(e) => handleInputChange("age", e.target.value)}
                    className="text-right"
                    dir="rtl"
                    type="number"
                  />
                </div>

                {/* מאיפה אתם בארץ */}
                <div className="space-y-2 text-right mt-6">
                  <Label htmlFor="location" className="text-base font-medium">מאיפה אתם בארץ (או בעולם)</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleInputChange("location", e.target.value)}
                    className="text-right"
                    dir="rtl"
                  />
                </div>

                {/* תחביבים או הרגלים בריאים */}
                <div className="space-y-2 text-right mt-6">
                  <Label htmlFor="hobbies" className="text-base font-medium">תחביבים או הרגלים בריאים שיש לכם</Label>
                  <Textarea
                    id="hobbies"
                    value={formData.hobbies}
                    onChange={(e) => handleInputChange("hobbies", e.target.value)}
                    className="text-right min-h-[100px]"
                    dir="rtl"
                  />
                </div>

                <Separator className="my-8" />

                {/* הרשאות */}
                <div className="space-y-6">
                  <h4 className="text-lg font-semibold">הרשאות</h4>
                  
                   <div className="flex items-center justify-between">
                     <Label className="text-base">הפיכת המייל שלי לציבורי</Label>
                     <Toggle
                       checked={formData.makeEmailPublic}
                       onChange={(checked) => handleInputChange("makeEmailPublic", checked)}
                     />
                   </div>

                   <div className="flex items-center justify-between">
                     <Label className="text-base">למנוע מחברים לשלוח לי הודעות</Label>
                     <Toggle
                       checked={formData.preventMessaging}
                       onChange={(checked) => handleInputChange("preventMessaging", checked)}
                     />
                   </div>

                   <div className="flex items-center justify-between">
                     <Label className="text-base">הצגת הפרופיל שלי במדריך החברים</Label>
                     <Toggle
                       checked={formData.showInDirectory}
                       onChange={(checked) => handleInputChange("showInDirectory", checked)}
                     />
                   </div>
                </div>

                {/* כפתורי שמירה וביטול */}
                <div className="flex gap-3 justify-end mt-8">
                  <Button variant="outline" onClick={handleCancel}>
                    ביטול
                  </Button>
                  <Button onClick={handleSave} disabled={isSaving}>
                    {isSaving ? <><Loader2 className="h-4 w-4 animate-spin ms-2" />שומר...</> : "שמירת שינויים"}
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="notifications" className="space-y-8 text-right">
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold">התראות</h3>
                  <div className="flex items-center gap-8 text-sm font-medium text-muted-foreground">
                    <span>אימייל</span>
                    <span>באפליקציה</span>
                  </div>
                </div>

                {/* פעילות חדשה */}
                <div className="space-y-2">
                  <h4 className="text-lg font-medium mb-4">פעילות חדשה</h4>
                  
                  <NotificationRow title="תגובות על הפוסטים שלי" notificationKey="commentsOnPosts" />
                  <NotificationRow title="תגובות לתגובות שלי" notificationKey="repliesToComments" />
                  <NotificationRow title="אזכורים" notificationKey="mentions" />
                  <NotificationRow title="הודעות ישירות" notificationKey="directMessages" />
                  <NotificationRow title="סיכום שבועי בימי חמישי" notificationKey="weeklyDigest" />
                  <NotificationRow title="לייקים על הפוסטים שלי" notificationKey="likesOnPosts" />
                  <NotificationRow title="לייקים על התגובות שלי" notificationKey="likesOnComments" />
                  <NotificationRow title="שידורים חיים/חדרים" notificationKey="liveStreams" />
                  <NotificationRow title="תוכן קורס חדש" notificationKey="newCourseContent" />
                  <NotificationRow title="סקרים" notificationKey="polls" />
                  <NotificationRow title="גיימיפיקיישן" notificationKey="gamification" />
                  <NotificationRow title="חברים חדשים מצטרפים" notificationKey="newMembers" />
                  <NotificationRow title="תוכן חדש מדווח לבדיקה" notificationKey="flaggedContent" />
                </div>

                <Separator className="my-8" />

                {/* פוסטים חדשים */}
                <div className="space-y-2">
                  <h4 className="text-lg font-medium mb-4">פוסטים חדשים</h4>
                  
                  <NotificationRow title="חל על כל המרחבים" notificationKey="allSpaces" />
                  
                  <div className="space-y-2 mt-4">
                    <NotificationRow title="הרגלים בריאים 💪" notificationKey="healthyHabitsSpace" isIndented />
                    <NotificationRow title="תזונה ותכונים" notificationKey="nutritionSpace" isIndented />
                    <NotificationRow title="כושר - המאמן האישי" notificationKey="fitnessSpace" isIndented />
                    <NotificationRow title="קהילה - ביחד אנחנו כוח" notificationKey="mindfulnessSpace" isIndented />
                    <NotificationRow title="מגרש המשחקים" notificationKey="recipesSpace" isIndented />
                    <NotificationRow title="הספרייה" notificationKey="motivationSpace" isIndented />
                    <NotificationRow title="איזוני כמויד משותפים" notificationKey="workoutPlansSpace" isIndented />
                    <NotificationRow title="🍯 שירות לקוחות" notificationKey="communitySpace" isIndented />
                    <NotificationRow title="ציוני כמויד משותפים" notificationKey="wellnessSpace" isIndented />
                  </div>
                </div>

                <Separator className="my-8" />

                {/* כפתורי פעולה */}
                <div className="flex gap-4 justify-between items-center">
                  <Button variant="outline" onClick={turnOffAllNotifications} className="text-destructive border-destructive hover:bg-destructive/10 active:bg-destructive/20">
                    כיבוי כל ההתראות
                  </Button>
                  <Button onClick={handleSave} disabled={isSaving}>
                    {isSaving ? <><Loader2 className="h-4 w-4 animate-spin ms-2" />שומר...</> : "שמירת שינויים"}
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="authentication" className="space-y-8 text-right">
              <div>
                <h3 className="text-2xl font-bold mb-6">אימות</h3>
                
                {/* אימייל וסיסמה */}
                <div className="space-y-6">
                  <h4 className="text-lg font-semibold">אימייל וסיסמה</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* אימייל */}
                    <div className="space-y-2 text-right">
                      <Label htmlFor="email" className="text-base font-medium">אימייל</Label>
                      <Input
                        id="email"
                        value={authData.email}
                        onChange={(e) => handleAuthChange("email", e.target.value)}
                        className="text-right"
                        dir="rtl"
                        type="email"
                      />
                    </div>

                    {/* סיסמה נוכחית */}
                    <div className="space-y-2 text-right">
                      <Label htmlFor="currentPassword" className="text-base font-medium">סיסמה נוכחית</Label>
                      <Input
                        id="currentPassword"
                        value={authData.currentPassword}
                        onChange={(e) => handleAuthChange("currentPassword", e.target.value)}
                        className="text-right"
                        dir="rtl"
                        type="password"
                      />
                    </div>

                    {/* סיסמה חדשה */}
                    <div className="space-y-2 text-right">
                      <Label htmlFor="newPassword" className="text-base font-medium">סיסמה חדשה</Label>
                      <Input
                        id="newPassword"
                        value={authData.newPassword}
                        onChange={(e) => handleAuthChange("newPassword", e.target.value)}
                        className="text-right"
                        dir="rtl"
                        type="password"
                      />
                    </div>

                    {/* אישור סיסמה */}
                    <div className="space-y-2 text-right">
                      <Label htmlFor="confirmPassword" className="text-base font-medium">אישור סיסמה</Label>
                      <Input
                        id="confirmPassword"
                        value={authData.confirmPassword}
                        onChange={(e) => handleAuthChange("confirmPassword", e.target.value)}
                        className="text-right"
                        dir="rtl"
                        type="password"
                      />
                    </div>
                  </div>
                </div>

                <Separator className="my-8" />

                {/* ביטול חשבון */}
                <div className="space-y-6">
                  <h4 className="text-lg font-semibold">ביטול חשבון</h4>
                  
                  <div className="p-6 border border-destructive/30 rounded-lg bg-destructive/10">
                    <p className="text-sm text-destructive mb-4 text-right">
                      זהירות! פעולה זו תבטל לצמיתות את החשבון שלך בקהילה.
                    </p>
                    <Button
                      variant="destructive"
                      onClick={handleDeactivateAccount}
                    >
                      ביטול החשבון שלי
                    </Button>
                  </div>
                </div>

                {/* כפתורי שמירה וביטול */}
                <div className="flex gap-3 justify-end mt-8">
                  <Button variant="outline" onClick={handleCancel}>
                    ביטול
                  </Button>
                  <Button onClick={handleSave} disabled={isSaving}>
                    {isSaving ? <><Loader2 className="h-4 w-4 animate-spin ms-2" />שומר...</> : "שמירת שינויים"}
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditProfileDialog;

